import { City } from '../types/chatTypes';
import { handleError } from '../../../../lib/utils/error';
import { searchTours } from '../../../tours/services/tourSearchService';
import { Tour } from '../../../tours/types'; // Import Tour type
import { AmadeusActivity } from '../../../../services/amadeus';
import { SimplifiedActivity } from '../../types';
import { locationStringToCity } from '../utils/contextUtils';

/**
 * Service for activity/tour-related functionality
 */
export class ActivityService {
  /**
   * Map from Tour object (returned by searchTours) to SimplifiedActivity object
   */
  private mapToSimplifiedActivity(tour: Tour, city?: City): SimplifiedActivity {
    // Safely access potentially undefined properties from Tour type
    const latitude = tour.location?.latitude;
    const longitude = tour.location?.longitude;
    const rating = tour.rating;
    const priceAmount = tour.price?.amount;
    const priceCurrency = tour.price?.currencyCode || 'USD';

    return {
      id: tour.id,
      name: tour.name || 'Unnamed Activity',
      shortDescription: tour.shortDescription,
      price: priceAmount ? { amount: priceAmount, currencyCode: priceCurrency } : undefined,
      rating: !isNaN(rating!) ? rating : undefined,
      pictures: tour.images, // Use tour.images instead of activity.pictures
      bookingLink: tour.bookingLink,
      latitude: !isNaN(latitude!) ? latitude : undefined,
      longitude: !isNaN(longitude!) ? longitude : undefined,
    };
  }

  /**
   * Search for activities and return simplified results
   */
  async searchActivities(location: string): Promise<{
    simplifiedActivities: SimplifiedActivity[];
    conversationalResponse: string;
    error?: string;
  }> {
    console.log(`[ActivityService] Starting activity search for location: "${location}"`);

    try {
      const city = await locationStringToCity(location);
      if (!city) {
        console.log(`[ActivityService] Could not convert "${location}" to a valid City object`);
        return {
          simplifiedActivities: [],
          conversationalResponse: `I couldn't find "${location}" as a valid destination for activities. Could you please provide a specific city name?`,
          error: 'invalid_location'
        };
      }

      // Validate coordinates which are crucial for activity search
      if (city.latitude === undefined || city.longitude === undefined) {
         console.log(`[ActivityService] Missing coordinates for "${city.name}", cannot search activities effectively.`);
         return {
           simplifiedActivities: [],
           conversationalResponse: `I couldn't pinpoint the exact location for "${city.name}" to find activities. Could you try a nearby major city?`,
           error: 'missing_coordinates'
         };
      }


      console.log(`[ActivityService] Calling searchTours for city: ${city.name}`);
      // Assuming searchTours takes similar parameters to searchHotels, adapt if needed
      const result = await searchTours({
        city: city,
        useSquareSearch: true, // Use square search by default
        squareSize: 20, // Default search radius/area
        pageSize: 10 // Limit results for chat
      });

      const activities = result.tours || []; // Assuming the result structure has a 'tours' array
      console.log(`[ActivityService] searchTours returned ${activities?.length ?? 0} results.`);

      if (!activities || activities.length === 0) {
        console.log(`[ActivityService] No activities found for ${location}`);
        return {
          simplifiedActivities: [],
          conversationalResponse: `I couldn't find any activities listed for ${location} right now.`,
          error: 'no_activities_found'
        };
      }

      // Map Tour objects to SimplifiedActivity
      const simplifiedActivities: SimplifiedActivity[] = activities.slice(0, 5).map(
          (tour: Tour) => this.mapToSimplifiedActivity(tour, city)
      );


      console.log(`[ActivityService] Mapped to ${simplifiedActivities.length} simplified activities.`);

      let conversationalResponse = `I found ${simplifiedActivities.length} interesting activities in ${city.name}.`;
      // Add more details like price range or top rated if desired

      return {
        simplifiedActivities,
        conversationalResponse
      };

    } catch (error) {
      console.error(`[ActivityService] Error searching activities for ${location}:`, error);
      handleError(error, `Activity search failed for ${location}`);
      return {
        simplifiedActivities: [],
        conversationalResponse: `Sorry, I encountered an error trying to find activities for ${location}.`,
        error: 'search_error'
      };
    }
  }
} 